import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Play, Pause, Square, Clock, User, Calendar, ArrowLeft } from 'lucide-react'

export default function TimeTrackingPage() {
  const navigate = useNavigate()
  const [timeLogs, setTimeLogs] = useState([
    { id: 1, task: 'Design Homepage', user: 'Sarah Chen', date: '2026-05-04', hours: 4.5, project: 'Website Redesign' },
    { id: 2, task: 'API Integration', user: 'Mike Johnson', date: '2026-05-04', hours: 6, project: 'Backend' },
    { id: 3, task: 'Testing', user: 'Emma Wilson', date: '2026-05-03', hours: 3, project: 'Mobile App' },
    { id: 4, task: 'Documentation', user: 'Alex Kumar', date: '2026-05-03', hours: 2.5, project: 'Docs' },
  ])

  const [isTimer, setIsTimer] = useState(false)
  const [timerSeconds, setTimerSeconds] = useState(0)
  const [activeTask, setActiveTask] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ task: '', user: '', hours: 0, project: '' })

  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const todayHours = timeLogs
      .filter(log => log.date === today)
      .reduce((sum, log) => sum + log.hours, 0)

    const totalHours = timeLogs.reduce((sum, log) => sum + log.hours, 0)

    return { todayHours, totalHours, totalLogs: timeLogs.length }
  }, [timeLogs])

  React.useEffect(() => {
    if (!isTimer) return
    const interval = setInterval(() => setTimerSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [isTimer])

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleStopTimer = () => {
    const hours = timerSeconds / 3600
    setTimeLogs([...timeLogs, {
      id: Date.now(),
      task: activeTask,
      user: 'You',
      date: new Date().toISOString().split('T')[0],
      hours: parseFloat(hours.toFixed(2)),
      project: 'Current'
    }])
    setIsTimer(false)
    setTimerSeconds(0)
    setActiveTask('')
  }

  const handleAddLog = () => {
    if (!formData.task) return
    setTimeLogs([...timeLogs, { id: Date.now(), date: new Date().toISOString().split('T')[0], ...formData }])
    setShowModal(false)
    setFormData({ task: '', user: '', hours: 0, project: '' })
  }

  const handleDeleteLog = (id) => {
    setTimeLogs(timeLogs.filter(log => log.id !== id))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Time Tracking</h1>
          <p className="text-slate-400">Log hours and track billable time</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Today's Hours</div>
            <div className="text-3xl font-bold text-blue-400">{stats.todayHours.toFixed(1)}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Hours</div>
            <div className="text-3xl font-bold text-green-400">{stats.totalHours.toFixed(1)}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Time Entries</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.totalLogs}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Billable Hours</div>
            <div className="text-3xl font-bold text-purple-400">{(stats.totalHours * 0.8).toFixed(1)}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-6">Timer</h3>
          <div className="flex items-center gap-4 mb-6">
            <div className="text-5xl font-mono text-blue-400 font-bold">{formatTime(timerSeconds)}</div>
            {!isTimer ? (
              <>
                <input
                  type="text"
                  value={activeTask}
                  onChange={(e) => setActiveTask(e.target.value)}
                  placeholder="What are you working on?"
                  className="flex-1 bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500"
                />
                <button onClick={() => setIsTimer(true)} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold">
                  <Play size={20} /> Start
                </button>
              </>
            ) : (
              <>
                <div className="flex-1 text-white text-lg font-semibold">{activeTask}</div>
                <button onClick={() => setIsTimer(false)} className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-semibold">
                  <Pause size={20} /> Pause
                </button>
                <button onClick={handleStopTimer} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold">
                  <Square size={20} /> Stop
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-4 mb-8">
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
            <Plus size={20} /> Add Entry
          </button>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20 bg-slate-900/50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Task</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Hours</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Project</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {timeLogs.map((log) => (
                  <tr key={log.id} className="border-b border-blue-500/10 hover:bg-slate-800/50">
                    <td className="px-6 py-4 text-white font-medium">{log.task}</td>
                    <td className="px-6 py-4 text-slate-400">{log.user}</td>
                    <td className="px-6 py-4 text-slate-400">{log.date}</td>
                    <td className="px-6 py-4 text-blue-400 font-semibold">{log.hours}h</td>
                    <td className="px-6 py-4 text-slate-400">{log.project}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleDeleteLog(log.id)} className="text-red-400 hover:text-red-300">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">Add Time Entry</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.task} onChange={(e) => setFormData({...formData, task: e.target.value})} placeholder="Task" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.user} onChange={(e) => setFormData({...formData, user: e.target.value})} placeholder="User" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.project} onChange={(e) => setFormData({...formData, project: e.target.value})} placeholder="Project" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="number" value={formData.hours} onChange={(e) => setFormData({...formData, hours: parseFloat(e.target.value)})} placeholder="Hours" step="0.5" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleAddLog} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Add Entry</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
